export type Subscription = { remove: () => void };

type Listener<Args extends unknown[]> = (...args: Args) => void;

/**
 * Minimal typed event emitter. `Events` maps each event name to its listener
 * arguments, e.g. `{ loaded: [info: AdLoadInfo] }`.
 */
export class EventEmitter<Events extends Record<string, unknown[]>> {
  private readonly listeners: { [E in keyof Events]?: Set<Listener<Events[E]>> } = {};

  /**
   * @param onListenerError Called when a listener throws, so one failing
   * listener doesn't stop the rest from being notified.
   */
  constructor(private readonly onListenerError: (error: unknown) => void) {}

  on<E extends keyof Events>(event: E, listener: Listener<Events[E]>): Subscription {
    const listeners = (this.listeners[event] ??= new Set());
    listeners.add(listener);
    return { remove: () => listeners.delete(listener) };
  }

  emit<E extends keyof Events>(event: E, ...args: Events[E]): void {
    // Copy first so a listener that unsubscribes mid-dispatch doesn't skip the next one.
    for (const listener of [...(this.listeners[event] ?? [])]) {
      try {
        listener(...args);
      } catch (error) {
        this.onListenerError(error);
      }
    }
  }
}
